import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { useStartProject } from "@/lib/startProjectContext";
import { ASK_TOPICS, flattenAskQuestions } from "@/data/askKnowledge";

/**
 * /ask — AI-search-optimized FAQ knowledge base.
 *
 * Single authoritative page — no thin sub-pages. Every question in the
 * FAQPage JSON-LD graph matches the visible text exactly (schema validity
 * requirement). Organised by topic for scannability.
 */
export const Ask = () => {
  const { open } = useStartProject();
  const all = flattenAskQuestions();
  const total = all.length;

  useSeo({
    title: "Ask · India Sourcing FAQ for US Buyers | Asia Sourcing",
    description:
      `${total} answers to the questions US buyers, brands, retailers and hospitality operators ask about sourcing from India. MOQ · tariffs · AQL · CPSIA · Prop 65 · freight · IP. Answer-first, primary-sourced.`,
    path: "/ask",
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          "@id": `${SITE_URL}/ask#faq`,
          url: `${SITE_URL}/ask`,
          name: "India Sourcing FAQ for US Buyers",
          about: [
            { "@type": "Thing", name: "India sourcing" },
            { "@type": "Thing", name: "Quality control" },
            { "@type": "Thing", name: "US import compliance" },
          ],
          mainEntity: all.map((q) => ({
            "@type": "Question",
            "@id": `${SITE_URL}/ask#${q.id}`,
            name: q.q,
            acceptedAnswer: { "@type": "Answer", text: q.a },
          })),
        },
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/ask`,
          name: "Ask · India Sourcing FAQ for US Buyers",
          url: `${SITE_URL}/ask`,
          isPartOf: { "@id": `${SITE_URL}#website` },
          publisher: { "@id": `${SITE_URL}#organization` },
          inLanguage: "en",
          about: "India sourcing questions answered for US buyers",
        },
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Ask", path: "/ask" },
        ]),
      ],
    },
    jsonLdId: "ask-jsonld",
  });

  return (
    <article data-testid="ask-page">
      {/* HERO */}
      <header
        className="wrap"
        style={{ paddingBlock: "calc(74px + var(--section)) 2rem" }}
      >
        <Reveal
          as="nav"
          aria-label="Breadcrumbs"
          style={{ marginBottom: "1.2rem" }}
        >
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              color: "var(--brass-deep)",
              letterSpacing: ".14em",
              textTransform: "uppercase",
            }}
          >
            <Link to="/" style={{ color: "inherit" }}>
              Home
            </Link>{" "}
            · Ask
          </p>
        </Reveal>
        <Reveal>
          <p className="eyebrow" data-testid="ask-eyebrow">
            AI-search knowledge base · {total} answers
          </p>
        </Reveal>
        <WordReveal
          as="h1"
          text="Ask us anything about sourcing from India."
          className="display tp-title"
          stagger={70}
          testId="ask-h1"
        />
        <Reveal
          as="p"
          delay={200}
          style={{
            maxWidth: "72ch",
            color: "var(--ink-soft)",
            fontSize: "1.1rem",
            marginTop: "1.4rem",
            lineHeight: 1.65,
          }}
        >
          Twenty-seven plain-English answers to the questions US retailers,
          brands, DTC founders and hospitality FF&amp;E procurement leads
          ask us on the first call. Answer-first, primary-sourced,
          continuously updated. Skim the topic links below or read straight
          through.
        </Reveal>
        <Reveal
          as="div"
          delay={280}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: ".8rem",
            marginTop: "2rem",
          }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="ask-cta-primary"
          >
            Start Your Project →
          </button>
          <Link
            to="/usa"
            className="btn btn-ghost"
            data-testid="ask-cta-usa"
          >
            See the US hub
          </Link>
        </Reveal>
      </header>

      {/* TOPIC NAV */}
      <section
        className="wrap"
        style={{
          paddingBlock: "clamp(1.4rem, 2.4vw, 2rem)",
          borderBlock: "1px solid var(--line)",
        }}
        data-testid="ask-topic-nav"
      >
        <p
          className="mono"
          style={{
            color: "var(--brass-deep)",
            fontSize: ".68rem",
            letterSpacing: ".14em",
            textTransform: "uppercase",
            marginBottom: "1rem",
          }}
        >
          Topics
        </p>
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexWrap: "wrap",
            gap: ".6rem",
          }}
        >
          {ASK_TOPICS.map((t) => (
            <li key={t.id}>
              <a
                href={`#topic-${t.id}`}
                style={{
                  display: "inline-block",
                  padding: ".45rem .8rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  fontFamily: "var(--mono)",
                  fontSize: ".72rem",
                  letterSpacing: ".06em",
                  textDecoration: "none",
                }}
              >
                {t.label} · {t.questions.length}
              </a>
            </li>
          ))}
        </ul>
      </section>

      {/* TOPICS + Q&A */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}
      >
        {ASK_TOPICS.map((topic) => (
          <Reveal
            key={topic.id}
            as="section"
            id={`topic-${topic.id}`}
            style={{
              marginTop: "3rem",
              paddingTop: "1rem",
              scrollMarginTop: "120px",
            }}
            data-testid={`ask-topic-${topic.id}`}
          >
            <p
              className="mono"
              style={{
                color: "var(--brass-deep)",
                fontSize: ".68rem",
                letterSpacing: ".14em",
                textTransform: "uppercase",
                margin: "0 0 .5rem",
              }}
            >
              Topic · {topic.questions.length} answers
            </p>
            <h2
              className="display"
              style={{
                fontSize: "clamp(1.6rem, 2.6vw, 2.1rem)",
                margin: "0 0 .8rem",
                letterSpacing: "-.01em",
                lineHeight: 1.15,
              }}
            >
              {topic.label}
            </h2>
            <p
              style={{
                color: "var(--ink-soft)",
                fontSize: "1.02rem",
                lineHeight: 1.7,
                margin: "0 0 1.6rem",
                maxWidth: "68ch",
              }}
            >
              {topic.intro}
            </p>

            {topic.questions.map((q) => (
              <article
                key={q.id}
                id={q.id}
                style={{
                  borderTop: "1px solid var(--line)",
                  paddingBlock: "1.6rem",
                  scrollMarginTop: "120px",
                }}
                data-testid={`ask-q-${q.id}`}
              >
                <h3
                  style={{
                    fontFamily: "var(--display)",
                    fontSize: "1.2rem",
                    margin: "0 0 .8rem",
                    letterSpacing: "-.005em",
                    lineHeight: 1.35,
                  }}
                >
                  {q.q}
                </h3>
                <p
                  style={{
                    color: "var(--ink-soft)",
                    fontSize: "1rem",
                    lineHeight: 1.75,
                    margin: 0,
                    maxWidth: "68ch",
                  }}
                >
                  {q.a}
                </p>
                {q.links && q.links.length > 0 && (
                  <p
                    style={{
                      marginTop: "1rem",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: ".6rem",
                      fontFamily: "var(--mono)",
                      fontSize: ".72rem",
                      letterSpacing: ".08em",
                      textTransform: "uppercase",
                    }}
                  >
                    {q.links.map((l) => (
                      <Link
                        key={l.to}
                        to={l.to}
                        style={{
                          color: "var(--brass-deep)",
                          textDecoration: "underline",
                        }}
                        data-testid={`ask-q-${q.id}-link`}
                      >
                        {l.label} →
                      </Link>
                    ))}
                  </p>
                )}
              </article>
            ))}
          </Reveal>
        ))}
      </section>

      {/* CTA */}
      <section
        className="wrap"
        style={{
          paddingBlock: "var(--section)",
          borderTop: "1px solid var(--line)",
        }}
      >
        <Reveal as="p" className="eyebrow">
          Still have a question?
        </Reveal>
        <Reveal as="h2" className="display" style={{ maxWidth: "34ch" }}>
          Send us your brief. One accountable team from brief to container seal.
        </Reveal>
        <Reveal
          as="div"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1rem",
            marginTop: "2rem",
          }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="ask-cta-start-project"
          >
            Start Your Project <ArrowRight size={16} strokeWidth={1.6} aria-hidden />
          </button>
          <Link
            to="/contact"
            className="btn btn-ghost"
            data-testid="ask-cta-contact"
          >
            Send your requirement
          </Link>
        </Reveal>
      </section>
    </article>
  );
};

export default Ask;
